import { navigate, PageProps } from 'gatsby';
import { ComponentProps, type MouseEvent, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, FileAnalysisCard, Header, Icon, MenuButton, SidebarLayout, SidebarMenu } from '@opswat/ui';
import { PageHeader } from 'components/layout/PageHeader';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { useUser } from 'services/User';
import { useUserData } from 'services/api/UserData';
import { DashboardTabs, IntegrationsTabs, SettingsTabs } from 'services/tabs';
import { EngineType } from '../../types/Workflows';
import { useGlobalDragDrop } from 'hooks/useGlobalDragDrop';
import { LoadingSpinner } from 'components/LoadingSpinner';
import { canAccessHomePage, canAccessPage } from 'services/accessControl';

const DEFAULT_WORKFLOW_ID = 'default';
const SidebarMenuItem = SidebarMenu.Item as (
    props: Omit<ComponentProps<typeof SidebarMenu.Item>, 'label'> & { label?: ReactNode; },
) => ReturnType<typeof SidebarMenu.Item>;

type LayoutProps = Omit<PageProps, 'children'> & { children?: ReactNode; };

export const Layout = ({ children, location }: LayoutProps) => {
    const pathname = location.pathname;
    const { t } = useTranslation();
    const { setGlobal } = useQueryFilter();
    const { user } = useUser();
    const { data: userData } = useUserData();
    const isPaidUser = userData?.licenseInfo?.paidUser === 1 || userData?.paid_user === 1;
    const organizationId = user?.organization?.organization_id;
    const { isDragOver, isUploading } = useGlobalDragDrop();

    useEffect(() => {
        setGlobal((prev) => {
            if ((!prev?.organizationIds || prev?.organizationIds?.length === 0) && organizationId) {
                return { ...prev, organizationIds: [organizationId] };
            }

            return prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    // Determine default settings tab based on user access
    const getDefaultSettingsTab = useCallback(() => {
        if (canAccessPage(user, 'profile')) return SettingsTabs.Profile;
        if (canAccessPage(user, 'security')) return SettingsTabs.Security;
        if (canAccessPage(user, 'notifications')) return SettingsTabs.Notifications;
        return SettingsTabs.Profile;
    }, [user]);

    const defaultSettingsTab = getDefaultSettingsTab();
    const canSeeCswgProxy = canAccessPage(user, 'cswg');
    const canSeeStore = canAccessPage(user, 'store');

    // Filter menu items based on user role
    const menuData = useMemo(() => {
        const allMenuData: Array<ComponentProps<typeof SidebarMenu.Item> & { page?: string; }> = [
            {
                label: t('Dashboard'),
                icon: 'dashboard',
                isActive: pathname.startsWith('/dashboard'),
                href: `/dashboard/${DashboardTabs.Security}`,
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate(`/dashboard/${DashboardTabs.Security}`);
                },
                page: 'dashboard',
            },
            {
                label: t('Reports'),
                icon: 'detail-small',
                isActive: pathname.startsWith('/results') || pathname.startsWith('/reports'),
                hasBorder: true,
                href: '/reports',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/reports');
                },
                page: 'reports',
            },
            {
                label: t('Analyze'),
                icon: 'fileProcessing',
                isActive: pathname.startsWith('/analyze'),
                hasBorder: true,
                href: '/analyze',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/analyze');
                },
                page: 'analyze',
            },
            {
                label: t('API Key'),
                icon: 'api',
                isActive: pathname.startsWith('/account/api-key'),
                hasBorder: true,
                href: '/account/api-key',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/account/api-key');
                },
                page: 'account/api-key',
            },
            {
                label: t('Integrations'),
                icon: 'connect',
                isActive: pathname.startsWith('/integrations'),
                hasBorder: true,
                href: `/integrations/${IntegrationsTabs.All}`,
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate(`/integrations/${IntegrationsTabs.All}`);
                },
                page: 'integrations',
            },
        ];

        return allMenuData.filter((item) => {
            // If no page property, show the item
            if (!item.page) return true;
            // Check if user has access to this page
            return canAccessPage(user, item.page);
        });
    }, [user, pathname, t]);

    const secondaryMenuData = useMemo((): Array<ComponentProps<typeof SidebarMenu.Item>> => {
        const settingsItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('Settings'),
            icon: 'settings',
            isActive: pathname.startsWith('/settings'),
            href: `/settings/${defaultSettingsTab}`,
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate(`/settings/${defaultSettingsTab}`);
            },
        };

        const cveVulnerabilitiesItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('CVE Vulnerabilities'),
            icon: 'base',
            isActive: pathname.startsWith('/cve-vulnerabilities'),
            href: '/cve-vulnerabilities',
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/cve-vulnerabilities');
            },
        };

        const licensingFreeItem: ComponentProps<typeof SidebarMenu.Item> = {
            label: t('Licensing'),
            icon: 'certificate',
            isActive: pathname.startsWith('/licensing'),
            href: '/licensing',
            onClick: (e: MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                navigate('/licensing');
            },
        };

        const items: Array<ComponentProps<typeof SidebarMenu.Item>> = [cveVulnerabilitiesItem, licensingFreeItem];

        // Store sits directly under Licensing and is only shown for online-store users.
        if (canSeeStore) {
            const storeItem: ComponentProps<typeof SidebarMenu.Item> = {
                label: t('Store'),
                icon: 'box',
                isActive: pathname.startsWith('/store'),
                href: '/store',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/store');
                },
            };
            items.push(storeItem);
        }

        if (canSeeCswgProxy) {
            const cswgProxyItem: ComponentProps<typeof SidebarMenu.Item> = {
                label: t('CSWG Proxy'),
                icon: 'internet',
                isActive: pathname.startsWith('/cswg'),
                href: '/cswg',
                onClick: (e: MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault();
                    navigate('/cswg');
                },
            };
            items.push(cswgProxyItem);
        }

        // Settings is always the last item in the secondary menu.
        items.push(settingsItem);

        return items;
    }, [canSeeCswgProxy, canSeeStore, defaultSettingsTab, pathname, t]);

    const workflowsMenuItems = useMemo(() => {
        const items = [
            {
                label: t('Workflows'),
                path: '/secure-pipeline-studio/workflows',
                isActive: pathname.startsWith('/secure-pipeline-studio/workflows'),
                page: 'workflows',
            },
            {
                label: t('Engines'),
                path: `/secure-pipeline-studio/engines/${EngineType.Filetype}/${DEFAULT_WORKFLOW_ID}`,
                isActive: pathname.startsWith('/secure-pipeline-studio/engines'),
                page: 'engines',
            },
        ];

        // Filter based on access control
        return items.filter((item) => canAccessPage(user, item.page));
    }, [user, pathname, t]);

    // Root must not be <main>: SidebarLayout.Content already renders the sole page <main>.
    // An outer <main> caused duplicate-main + banner-inside-main (WCAG landmark) violations.
    return (
        <div
            data-testid='layout-app-shell'
            className={`tw:font-roboto tw:flex tw:flex-col tw:h-screen ${isDragOver || isUploading ? 'tw:opacity-50' : ''}`}
        >
            <SidebarLayout>
                <SidebarLayout.Header>
                    <Header.Product productSubtitle='Cloud' />
                    <Header.Actions>
                        <div className='tw:flex tw:items-center tw:gap-4'>
                            <MenuButton>
                                <MenuButton.Trigger
                                    onClick={() => {
                                        if (canAccessHomePage(user)) {
                                            navigate('/', { state: { fromHomeButton: true } });
                                        } else {
                                            navigate(`/dashboard/${DashboardTabs.Security}`);
                                        }
                                    }}
                                    data-testid='layout-home-button'
                                    className='tw:border-transparent tw:dark:border-transparent tw:p-8'
                                >
                                    <Icon iconName='home' />
                                </MenuButton.Trigger>
                            </MenuButton>
                            <PageHeader />
                        </div>
                    </Header.Actions>
                </SidebarLayout.Header>

                <SidebarLayout.Menu>
                    <SidebarMenu>
                        {menuData.map((props) => (
                            <SidebarMenu.Item key={props.label} {...props} />
                        ))}
                        {isPaidUser && workflowsMenuItems.length > 0 && (
                            <SidebarMenu.Item
                                label={t('Secure Pipeline Studio')}
                                icon='target'
                                isActive={pathname.startsWith('/secure-pipeline-studio')}
                                hasBorder={true}
                                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                    // Navigate to first accessible workflow item
                                    const firstAccessiblePath = workflowsMenuItems[0]?.path;
                                    if (firstAccessiblePath) {
                                        navigate(firstAccessiblePath);
                                    }
                                }}
                            >
                                {workflowsMenuItems.map((item) => (
                                    <SidebarMenu.Item
                                        key={item.page}
                                        label={item.label}
                                        isActive={item.isActive}
                                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                                            e.preventDefault();
                                            navigate(item.path);
                                        }}
                                    />
                                ))}
                            </SidebarMenu.Item>
                        )}
                        {!isPaidUser && (
                            <SidebarMenuItem
                                key='secure-pipeline-upsell'
                                label={(
                                    <div className='tw:flex tw:items-center tw:justify-between tw:gap-8 tw:w-full'>
                                        <span>{t('Secure Pipeline')}</span>
                                        <Badge variant='secure' text={t('Paid')} />
                                    </div>
                                )}
                                icon='target'
                                isActive={pathname.startsWith('/secure-pipeline-studio')}
                                hasBorder={true}
                                onClick={() => navigate('/secure-pipeline-studio/workflows')}
                            />
                        )}
                        {secondaryMenuData.map((props) => (
                            <SidebarMenu.Item key={props.label} {...props} />
                        ))}
                    </SidebarMenu>
                    <div className='tw:pt-8' data-sidebar-extra>
                        <FileAnalysisCard />
                    </div>
                </SidebarLayout.Menu>

                <SidebarLayout.Content className='tw:min-w-0'>{children}</SidebarLayout.Content>
            </SidebarLayout>

            {isUploading && (
                <div className='tw:fixed tw:inset-0 tw:z-50 tw:flex tw:items-center tw:justify-center'>
                    <LoadingSpinner />
                </div>
            )}
        </div>
    );
};
